import React from 'react';
import i18n from '@/i18n/config';
import { Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, Button, Select, SelectItem } from '@heroui/react';

interface DomainStatusModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  currentStatus: string;
  domainId: number;
  onConfirm: (newStatus: string) => Promise<void>;
}

export function DomainStatusModal({
  isOpen,
  onOpenChange,
  currentStatus,
  domainId,
  onConfirm
}: DomainStatusModalProps) {
  const [selectedStatus, setSelectedStatus] = React.useState<string>(currentStatus);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (isOpen) {
      setSelectedStatus(currentStatus);
    }
  }, [isOpen, currentStatus]);

  const handleConfirm = React.useCallback(async () => {
    if (selectedStatus === currentStatus) {
      onOpenChange(false);
      return;
    }

    try {
      setIsSubmitting(true);
      await onConfirm(selectedStatus);
      onOpenChange(false);
    } catch (error) {
      // Error handled by axios interceptor
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedStatus, currentStatus, onConfirm, onOpenChange]);

  return (
    <Modal isOpen={isOpen} placement="center" onOpenChange={onOpenChange} isDismissable={false}>
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex flex-col gap-1">Change Domain Status</ModalHeader>
            <ModalBody>
              <Select
                label={i18n.t('filters.status')}
                selectedKeys={[selectedStatus]}
                onSelectionChange={(keys) => {
                  const selected = Array.from(keys)[0] as string;
                  setSelectedStatus(selected);
                }}
              >
                <SelectItem key="verified">{i18n.t('filters.verified')}</SelectItem>
                <SelectItem key="pending">{i18n.t('enums.pending')}</SelectItem>
              </Select>
            </ModalBody>
            <ModalFooter>
              <Button variant="light" onPress={onClose} disabled={isSubmitting}>{i18n.t('modals.exportConfirm.cancel')}</Button>
              <Button color="primary" onPress={handleConfirm} isLoading={isSubmitting}>{i18n.t('common.save')}</Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}
